import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { SocketEvent, getStatusLabel } from '@swtorbit/shared-socket-events';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { Modal } from '@swtorbit/ui/react';
import { useGameContext } from '../contexts/GameContext';
import {
  getAvailableTransitions,
  type LifecycleTransition,
} from '../utils/lifecycleTransitions';

interface LifecycleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** A transition's button: primary is the default cyan, secondary an outline, danger red. */
const TRANSITION_BUTTON: Record<'primary' | 'secondary' | 'danger', string> = {
  primary: 'btn',
  secondary: 'btn btn--outline btn--slate',
  danger: 'btn btn--red',
};

export function LifecycleModal({ isOpen, onClose }: LifecycleModalProps) {
  const { selectedGame, startGame } = useGameContext();
  const { socket } = useSocketContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [pendingWarnings, setPendingWarnings] = useState<string[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const prevStatusRef = useRef(selectedGame?.status);

  const transitions = useMemo(
    () => (selectedGame ? getAvailableTransitions(selectedGame) : []),
    [selectedGame],
  );

  const selectedTransition: LifecycleTransition | undefined = useMemo(
    () => transitions.find((t) => t.id === selectedId) ?? transitions[0],
    [transitions, selectedId],
  );

  const conditionResult = useMemo(() => {
    if (!selectedTransition?.checkConditions || !selectedGame) {
      return { met: true, missingItems: [] };
    }
    return selectedTransition.checkConditions(selectedGame, []);
  }, [selectedTransition, selectedGame]);

  const preWarnings = useMemo(
    () => (selectedTransition?.checkWarnings && selectedGame ? selectedTransition.checkWarnings(selectedGame) : []),
    [selectedTransition, selectedGame],
  );

  // Reset state when modal opens/closes or game changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setTransitionError(null);
      setPendingWarnings([]);
      setIsExecuting(false);
      setShowConfirm(false);
    }
  }, [isOpen]);

  // Auto-close when game status changes (non-GAME_STATUS_UPDATED path, e.g. startGame)
  useEffect(() => {
    if (!isExecuting || !isOpen) return;
    const current = selectedGame?.status;
    if (current !== prevStatusRef.current) {
      setIsExecuting(false);
      if (pendingWarnings.length === 0) onClose();
    }
    prevStatusRef.current = current;
  }, [selectedGame?.status, isExecuting, isOpen, pendingWarnings.length, onClose]);

  useEffect(() => {
    prevStatusRef.current = selectedGame?.status;
  }, [selectedGame?.dbId]);

  // Listen for GAME_STATUS_UPDATED response (standard transition path)
  useEffect(() => {
    if (!socket || !isOpen) return;

    const handler = (response: {
      success: boolean;
      warnings?: string[];
      error?: string;
    }) => {
      setIsExecuting(false);
      if (!response.success) {
        setTransitionError(response.error ?? 'Transition failed. Please try again.');
        return;
      }
      if (response.warnings?.length) {
        setPendingWarnings(response.warnings);
      } else {
        onClose();
      }
    };

    socket.on(SocketEvent.GAME_STATUS_UPDATED, handler);
    return () => {
      socket.off(SocketEvent.GAME_STATUS_UPDATED, handler);
    };
  }, [socket, isOpen, onClose]);

  const handleActivate = useCallback(() => {
    if (!selectedTransition || !selectedGame || !socket) return;
    setShowConfirm(false);
    setTransitionError(null);
    setIsExecuting(true);
    selectedTransition.execute({ game: selectedGame, socket, startGame });
  }, [selectedTransition, selectedGame, socket, startGame]);

  const handleDismissWarnings = useCallback(() => {
    setPendingWarnings([]);
    onClose();
  }, [onClose]);

  if (!selectedGame) return null;

  const statusInfo = getStatusLabel(selectedGame.status);
  const canActivate = conditionResult.met && !isExecuting;

  return (
    <>
      <Modal open={isOpen} onClose={onClose} size="md" title={<>Game Lifecycle — {statusInfo.icon} {statusInfo.label}</>}>
          <div className="d-flex gap-lg">
            {/* Left: transition list */}
            <div className="d-flex flex-column gap-2xs border-end border-teal-dark pe-base">
              {transitions.map((t) => (
                <button
                  key={t.id}
                  className={[
                    'list__item',
                    t.id === (selectedTransition?.id ?? transitions[0]?.id) ? 'is-selected' : '',
                    t.buttonVariant === 'danger' ? 'text-red' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    setSelectedId(t.id);
                    setTransitionError(null);
                    setShowConfirm(false);
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Right: detail + activate */}
            <div className="d-flex flex-column gap-sm flex-1">
              {selectedTransition ? (
                <>
                  <h4 className="text-white mb-xs">{selectedTransition.label}</h4>
                  <p className="text-grey-light fs-sm">
                    {selectedTransition.description}
                  </p>

                  {/* Pending warnings (post-activation) */}
                  {pendingWarnings.length > 0 && (
                    <div className="d-flex flex-column gap-2xs mt-xs">
                      <p className="text-amber fs-sm fw-bold">
                        Game started with warnings:
                      </p>
                      <ul className="d-flex flex-column gap-2xs">
                        {pendingWarnings.map((w, i) => (
                          <li key={i} className="alert alert--red alert--sm fs-xs text-amber">
                            ⚠ {w}
                          </li>
                        ))}
                      </ul>
                      <div className="d-flex justify-content-end gap-xs mt-auto mt-xs">
                        <button className="btn" onClick={handleDismissWarnings}>
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Unmet conditions */}
                  {pendingWarnings.length === 0 && !conditionResult.met && (
                    <div className="mt-xs">
                      <p className="text-red fs-sm fw-bold mb-2xs">
                        {selectedTransition.conditionsLabel ??
                          'The following players must complete registration before proceeding:'}
                      </p>
                      <ul className="d-flex flex-column gap-2xs">
                        {conditionResult.missingItems.map((item, i) => (
                          <li key={i} className="alert alert--red alert--sm fs-xs">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings before going ahead (they don't block it) */}
                  {pendingWarnings.length === 0 && preWarnings.length > 0 && (
                    <ul className="d-flex flex-column gap-2xs mt-xs">
                      {preWarnings.map((w, i) => (
                        <li key={i} className="alert alert--sm fs-xs text-amber">
                          ⚠ {w}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Transition error */}
                  {transitionError && (
                    <p className="text-red fs-sm mt-xs">⚠ {transitionError}</p>
                  )}

                  {/* Activate / inline confirm */}
                  {pendingWarnings.length === 0 && (
                    <div className="d-flex justify-content-end gap-xs mt-auto">
                      {showConfirm ? (
                        <>
                          <button
                            className="btn btn--outline btn--slate"
                            onClick={() => setShowConfirm(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className={TRANSITION_BUTTON[selectedTransition.buttonVariant]}
                            disabled={isExecuting}
                            onClick={handleActivate}
                          >
                            {isExecuting ? 'Activating…' : 'Confirm'}
                          </button>
                        </>
                      ) : (
                        <button
                          className={TRANSITION_BUTTON[selectedTransition.buttonVariant]}
                          disabled={!canActivate}
                          onClick={() => setShowConfirm(true)}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-slate fs-sm">
                  Select an option to see details.
                </p>
              )}
            </div>
          </div>
        </Modal>
    </>
  );
}
